import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OWNERSHIP, V_STATUS, DueCell, FormDialog, opts } from "@/components/fleet/fleetConst";
import { FleetImport } from "@/components/fleet/FleetImport";

const DEFAULTS = { company_id: "", plate: "", model: "", ownership: "PROPRIETA", lessor: "", contract_due: null, assignee_name: "", employee_id: null, team_id: null, branch_id: null,
  assigned_at: null, insurer: "", insurance_due: null, revision_due: null, tax_due: null, current_km: 0, status: "ACTIVE", notes: "" };

const useFields = (form) => {
  const { visibleCompanies, teams, employees, branches } = useData();
  return [
    { key: "company_id", label: "Società", type: "select", options: visibleCompanies.map((c) => ({ value: c.id, label: c.legal_name })) },
    { key: "plate", label: "Targa" }, { key: "model", label: "Marca e modello" },
    { key: "ownership", label: "Tipologia", type: "select", options: opts(OWNERSHIP) }, { key: "lessor", label: "Società noleggio / leasing" },
    { key: "contract_due", label: "Scadenza contratto", type: "date" }, { key: "status", label: "Stato", type: "select", options: opts(V_STATUS) },
    { key: "team_id", label: "Squadra assegnataria (furgone-magazzino)", type: "select", optional: true, options: teams.map((t) => ({ value: t.id, label: t.name })) },
    { key: "employee_id", label: "Dipendente assegnatario", type: "select", optional: true, options: employees.map((e) => ({ value: e.id, label: e.name })) },
    { key: "assignee_name", label: "Assegnatario (testo dal file)" },
    { key: "branch_id", label: "Sede", type: "select", optional: true, options: branches.filter((b) => b.company_id === form.company_id).map((b) => ({ value: b.id, label: b.name })) },
    { key: "assigned_at", label: "Data assegnazione", type: "date" }, { key: "insurer", label: "Compagnia assicurativa" },
    { key: "insurance_due", label: "Scadenza assicurazione", type: "date" }, { key: "revision_due", label: "Scadenza revisione", type: "date" },
    { key: "tax_due", label: "Scadenza bollo", type: "date" }, { key: "current_km", label: "Km attuali", type: "number" },
    { key: "notes", label: "Note", wide: true },
  ];
};

const Assignee = ({ v }) => {
  const { nameOf } = useData();
  if (v.team_id) return <span className="font-semibold">{nameOf("teams", v.team_id)}</span>;
  if (v.employee_id) return nameOf("employees", v.employee_id);
  if (v.branch_id) return <span className="text-muted-foreground">{nameOf("branches", v.branch_id)}</span>;
  return v.assignee_name ? <span title="Da collegare a dipendente o squadra">{v.assignee_name} <span className="text-[10px] uppercase text-amber-700">da collegare</span></span> : "—";
};

export const VehiclesTab = ({ vehicles, onChanged, onOpenVehicle }) => {
  const { can } = useAuth();
  const { nameOf, visibleCompanies, companyId } = useData();
  const [q, setQ] = useState("");
  const [company, setCompany] = useState(companyId);
  useEffect(() => { setCompany(companyId); }, [companyId]);
  const [status, setStatus] = useState("live");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [importOpen, setImportOpen] = useState(false);
  const fields = useFields(form);
  const shown = useMemo(() => vehicles.filter((v) => (!company || v.company_id === company)
    && (status === "all" || (status === "live" ? !["SCRAPPED", "RETURNED", "SOLD"].includes(v.status) : v.status === status))
    && (!q || `${v.plate} ${v.model} ${v.assignee_name} ${v.lessor}`.toLowerCase().includes(q.toLowerCase()))), [vehicles, q, company, status]);
  const open = (v) => { setEditing(v || "new"); setForm(v ? { ...DEFAULTS, ...v } : { ...DEFAULTS, company_id: company }); };
  const save = async () => {
    try {
      const body = Object.fromEntries(Object.keys(DEFAULTS).map((k) => [k, form[k] === "" && k.endsWith("_due") ? null : form[k]]));
      if (editing === "new") await api.post("/fleet/vehicles", body); else await api.put(`/fleet/vehicles/${editing.id}`, body);
      toast.success("Mezzo salvato");
      setEditing(null);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const write = can("fleet:write");
  return (
    <div data-testid="vehicles-tab">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input className="w-64 bg-white" placeholder="Cerca targa, modello, assegnatario" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vehicles-search" />
        <Select value={company || "all"} onValueChange={(v) => setCompany(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56 bg-white" data-testid="vehicles-company-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le società</SelectItem>{visibleCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 bg-white" data-testid="vehicles-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="live">Mezzi in flotta</SelectItem><SelectItem value="all">Tutti</SelectItem>{Object.entries(V_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground" data-testid="vehicles-count">{shown.length} mezzi</span>
        {write && <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="fleet-import-button"><Upload className="mr-1 h-4 w-4" />Importa da Excel</Button>
          <Button onClick={() => open(null)} data-testid="vehicle-add-button"><Plus className="mr-1 h-4 w-4" />Nuovo mezzo</Button>
        </div>}
      </div>
      {shown.length === 0 ? <Empty text="Nessun mezzo" testId="vehicles-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Targa</TableHead><TableHead>Modello</TableHead><TableHead>Società</TableHead><TableHead>Tipologia</TableHead><TableHead>Assegnatario</TableHead><TableHead>Assicurazione</TableHead><TableHead>Revisione</TableHead><TableHead>Bollo</TableHead><TableHead>Contratto</TableHead><TableHead>Stato</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {shown.map((v) => (
                <TableRow key={v.id} className="cursor-pointer" onClick={() => onOpenVehicle(v.id)} data-testid={`vehicle-row-${v.plate}`}>
                  <TableCell className="font-mono text-sm font-bold">{v.plate}</TableCell>
                  <TableCell className="text-sm">{v.model}</TableCell>
                  <TableCell className="text-xs">{nameOf("companies", v.company_id, "code")}</TableCell>
                  <TableCell className="text-xs">{OWNERSHIP[v.ownership]}{v.lessor ? ` · ${v.lessor}` : ""}</TableCell>
                  <TableCell className="text-sm"><Assignee v={v} /></TableCell>
                  <TableCell><DueCell date={v.insurance_due} level={v.levels.insurance_due} /></TableCell>
                  <TableCell><DueCell date={v.revision_due} level={v.levels.revision_due} /></TableCell>
                  <TableCell><DueCell date={v.tax_due} level={v.levels.tax_due} /></TableCell>
                  <TableCell>{v.ownership === "PROPRIETA" ? "—" : <DueCell date={v.contract_due} level={v.levels.contract_due} />}</TableCell>
                  <TableCell className="text-xs">{V_STATUS[v.status]}</TableCell>
                  <TableCell>{write && <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); open(v); }} data-testid={`vehicle-edit-${v.plate}`}><Pencil className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <FormDialog open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Nuovo mezzo" : `Modifica ${form.plate}`} fields={fields} form={form} setForm={setForm} onSave={save} testPrefix="vehicle" />
      <FleetImport open={importOpen} onClose={() => setImportOpen(false)} onDone={onChanged} />
    </div>
  );
};
